import { cn } from "@frontend/shared/lib/utils";
import { cva } from "class-variance-authority";
import { format, isValid, parse } from "date-fns";
import * as React from "react";

const inputVariants = cva({
  variants: {
    variant: {
      default: "",
      error: "border-destructive focus-visible:ring-destructive",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

interface DateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  variant?: "default" | "error";
}

const DISPLAY_FORMAT = "MM' / 'dd' / 'yyyy";

/** Formats partial keystrokes into `MM / DD / YYYY` as the user types. */
const formatDateInput = (input: string) => {
  const cleaned = input.replace(/\D/g, "");

  let formatted = "";
  if (cleaned.length > 0) formatted += cleaned.slice(0, 2);
  if (cleaned.length > 2) formatted += ` / ${cleaned.slice(2, 4)}`;
  if (cleaned.length > 4) formatted += ` / ${cleaned.slice(4, 8)}`;

  return formatted;
};

/**
 * Normalizes a complete date string (e.g. `07-13-2003`, `07/13/2003`,
 * `07 / 13 / 2003`) into the display format `MM / DD / YYYY`.
 * Only acts on strings with exactly 8 digits so partial values typed
 * by the user pass through unchanged.
 */
const normalizeDateValue = (input: string): string => {
  const digits = input.replace(/\D/g, "");
  if (digits.length !== 8) return input;

  const cleaned = input.replace(/\s/g, "");
  for (const fmt of ["MM/dd/yyyy", "MM-dd-yyyy"]) {
    const parsed = parse(cleaned, fmt, new Date());
    if (isValid(parsed)) return format(parsed, DISPLAY_FORMAT);
  }
  return input;
};

const DateStorefrontInput = React.forwardRef<HTMLInputElement, DateInputProps>(
  ({ className, variant, onChange, value, ...props }, ref) => {
    const inputRef = React.useRef<HTMLInputElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatDateInput(e.target.value);

      const syntheticEvent = {
        ...e,
        target: { ...e.target, value: formatted },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(syntheticEvent);
    };

    const formattedValue =
      typeof value === "string" ? normalizeDateValue(value) : value;

    return (
      <input
        type="text"
        ref={inputRef}
        className={cn(inputVariants({ className }))}
        onChange={handleChange}
        value={formattedValue}
        placeholder="MM / DD / YYYY"
        maxLength={14}
        inputMode="numeric"
        autoCapitalize="none"
        autoCorrect="off"
        autoComplete="bday"
        spellCheck={false}
        {...props}
      />
    );
  },
);
DateStorefrontInput.displayName = "DateStorefrontInput";

export {
  DateStorefrontInput,
  formatDateInput,
  normalizeDateValue,
  type DateInputProps,
};
